import React, { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

/**
 * A box to sign in with a finger, pen or mouse. Reports the drawing as a PNG data URL, or null while
 * it's empty (a few dots don't count as a signature).
 */
export function SignaturePad({ onChange, height = 160 }: { onChange: (dataUrl: string | null) => void; height?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const inked = useRef(0);
  const [empty, setEmpty] = useState(true);

  // Sharp lines on high-density screens: size the canvas in device pixels
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const w = c.offsetWidth;
    c.width = w * ratio;
    c.height = height * ratio;
    const ctx = c.getContext("2d")!;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1e3a8a";
  }, [height]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const down = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = point(e);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !last.current) return;
    const p = point(e);
    const ctx = e.currentTarget.getContext("2d")!;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    inked.current += Math.hypot(p.x - last.current.x, p.y - last.current.y);
    last.current = p;
  };

  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    const enough = inked.current > 40;
    setEmpty(!enough);
    onChange(enough ? canvasRef.current!.toDataURL("image/png") : null);
  };

  const clear = () => {
    const c = canvasRef.current!;
    c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    inked.current = 0;
    setEmpty(true);
    onChange(null);
  };

  return (
    <div className="relative rounded-lg border-2 border-dashed border-border bg-white">
      <canvas
        ref={canvasRef}
        style={{ height, touchAction: "none" }}
        className="w-full block cursor-crosshair rounded-lg"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        aria-label="Signature box: draw your signature"
      />
      {empty && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">Sign here</span>
      )}
      <div className="absolute left-4 right-4 bottom-8 border-b border-border pointer-events-none" />
      <button
        type="button"
        onClick={clear}
        className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md bg-white/90 border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <Eraser className="size-3.5" /> Clear
      </button>
    </div>
  );
}
